import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {defer, forkJoin, Observable, throwError} from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import {ComposerStateService, ModelConfig} from '@app/model-composer';
import { CompositionSelectionService } from '@app/model-composer/services/composition-selection.service';

@Injectable({ providedIn: 'root' })
export class ModelConfigService {
  private readonly _config = signal<ModelConfig | null>(null);
  readonly config = this._config.asReadonly();

  private readonly _loading = signal(false);
  readonly loading = this._loading.asReadonly();

  private readonly selectionService = inject(CompositionSelectionService);
  private readonly state = inject(ComposerStateService);

  constructor(private readonly http: HttpClient) {}

  load(path: string, compositionId?: number): Observable<ModelConfig> {
    this._loading.set(true);
    return this.http.get<any>(path).pipe(
      map(({ data }) => {
        const { customizedConfiguration, ...config } = data;
        const normalizedConfig = this.normalizeConfig(config as ModelConfig);
        this._config.set(normalizedConfig);
        this.selectionService.applyDefaultsFromStructure(normalizedConfig);
        if (customizedConfiguration) {
          this.state.setComposition(customizedConfiguration.composition ?? [], compositionId ?? null);
          this.selectionService.setSelectionState({
            elementOverrides: customizedConfiguration.elementOverrides ?? {},
            overrides: customizedConfiguration.overrides ?? {},
            rowMeta: customizedConfiguration.rowMeta ?? {},
            revision: customizedConfiguration.revision,
          });
        }
        return normalizedConfig;
      }),
      catchError((error) => throwError(() => error)),
      finalize(() => { this._loading.set(false); })
    );
  }

  private loadAssetText(path: string): Observable<any> {
    return defer(() => fetch(path).then(res => {
      if (!res.ok) {
        throw new Error(`Asset not found: ${path}`);
      }
      return res.json();
    }));
  }

  private normalizeConfig(config: any): any {
    const materialColorList = config?.materialColorList;
    if (!Array.isArray(materialColorList) || materialColorList.length === 0) {
      return config;
    }
    const materialColorByCode = this.buildMaterialColorDictionary(materialColorList);
    this.removeUpdateAlsoFromGlobalCustomization(config?.customization);
    // this.mockPlacementToRemoveAndAddForGlobalCustomization(config);
    this.normalizeCustomizationGroups(config?.customization, materialColorByCode);
    const elementList = this.extractElementList(config);
    for (const element of elementList) {
      this.normalizeCustomizationGroups(element?.customization, materialColorByCode, config?.customization);
    }
    return config;
  }

  private buildMaterialColorDictionary(materialColorList: any[]): Record<string, any> {
    const materialColorByCode: Record<string, any> = Object.create(null);
    for (const materialColor of materialColorList) {
      const codeMaterialColor = materialColor?.codeMaterialColor;
      if (!codeMaterialColor) { continue; }
      materialColorByCode[codeMaterialColor] = materialColor;
    }
    return materialColorByCode;
  }

  private extractElementList(config: any): any[] {
    const elements =
      config?.elements ??
      config?.elementList ??
      config?.modelElements ??
      [];
    return Array.isArray(elements) ? elements : [];
  }

  private removeUpdateAlsoFromGlobalCustomization(customizationGroups: any[] | undefined): void {
    if (!Array.isArray(customizationGroups) || customizationGroups.length === 0) {
      return;
    }
    for (const customizationGroup of customizationGroups) {
      const customizationRows = customizationGroup?.customizationRows;
      if (!Array.isArray(customizationRows) || customizationRows.length === 0) {
        continue;
      }
      for (const customizationRow of customizationRows) {
        if (!customizationRow) { continue; }
        delete customizationRow.updateAlso;
      }
    }
  }

  private normalizeCustomizationGroups(customizationGroups: any[] | undefined, materialColorByCode: Record<string, any>, globalCustomization?: any): void {
    if (!Array.isArray(customizationGroups) || customizationGroups.length === 0) {
      return;
    }
    for (const customizationGroup of customizationGroups) {
      const customizationRows = customizationGroup?.customizationRows;
      if (!Array.isArray(customizationRows) || customizationRows.length === 0) {
        continue;
      }
      for (const customizationRow of customizationRows) {
        if (!customizationRow) { continue; }
        this.resolveDefaultSelection(customizationRow, materialColorByCode, globalCustomization);
        this.normalizeMaterialOptions(customizationRow, materialColorByCode);
      }
    }
  }

  private resolveDefaultSelection(customizationRow: any, materialColorByCode: Record<string, any>, globalCustomization?: any): void {
    const defaultOption = customizationRow?.default?.option;
    if (!defaultOption) {
      return;
    }
    let selectedValue = defaultOption.selected;
    if (typeof selectedValue !== 'string') {
      return;
    }
    if (!selectedValue) {
      const rows = globalCustomization?.[0]?.customizationRows ?? [];
      const linkedSelectedValue = rows.find((row: any) => customizationRow?.updateAlso?.includes(row?.id))?.default?.option?.selected?.codeMaterialColor;
      if (linkedSelectedValue) {
        selectedValue = linkedSelectedValue;
      }
    }
    defaultOption.selected = materialColorByCode[selectedValue] ?? selectedValue;
  }

  private normalizeMaterialOptions(customizationRow: any, materialColorByCode: Record<string, any>): void {
    const materialOptions = customizationRow?.materialOptions;
    if (!Array.isArray(materialOptions) || materialOptions.length === 0) {
      return;
    }
    for (const materialOption of materialOptions) {
      if (!materialOption) { continue; }
      const materialColorList = materialOption.materialColorList;
      if (!Array.isArray(materialColorList) || materialColorList.length === 0) {
        continue;
      }
      if (typeof materialColorList[0] !== 'string') {
        continue;
      }
      const etoMaterialColorSet = new Set(Array.isArray(materialOption.etoMaterialColorList) ? materialOption.etoMaterialColorList : []);
      const normalizedMaterialColorList: any[] = [];
      for (const materialColorCode of materialColorList) {
        const normalizedMaterialColor = materialColorByCode[materialColorCode];
        if (normalizedMaterialColor) {
          normalizedMaterialColorList.push({
            ...normalizedMaterialColor,
            eto: etoMaterialColorSet.has(materialColorCode),
          });
        }
      }
      materialOption.materialColorList = normalizedMaterialColorList;
    }
  }
}
